import { ArrowRightIcon, Button, Header, NicknameIcon, Paragraph, TextInput } from "@datagame/ui";
import { ChangeEvent, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import classNames from "classnames/bind";

import { LeaderboardConfiguration } from "../../../../gql/graphql";

import styles from "../NicknameModal.module.scss";
import { IS_PROJECT_NICKNAME_TAKEN } from "../../../../graphql/queries/Project";

const cx = classNames.bind(styles);

export interface ProjectNicknameModalProps {
  isOpen: boolean;
  configuration: LeaderboardConfiguration;
  onContinue: (nickname: string) => void;
  projectId: string;
}

export const ProjectNicknameModal = (props: ProjectNicknameModalProps) => {
  const { isOpen, configuration, onContinue, projectId } = props;
  const { t } = useTranslation("leaderboard");
  const isDesktop = useMediaQuery({ query: "(min-width: 1280px)" });
  const [nickname, setNickname] = useState("");
  const [error, setError] = useState<string | undefined>();
  const { nicknameQuestion, nicknameQuestionColor, nicknameLabel } = configuration;

  const { refetch } = useQuery(IS_PROJECT_NICKNAME_TAKEN, {
    variables: {
      nickname,
      projectId,
    },
    skip: true,
  });

  const handleContinue = async () => {
    if (!nickname) {
      setError(t("nicknameModal.input.errors.empty") as string);
      return;
    }

    const {
      data: { projectIsNicknameTaken },
    } = await refetch();

    if (projectIsNicknameTaken) {
      setError(t("nicknameModal.input.errors.taken", { nickname }) as string);
      return;
    }

    onContinue(nickname);
  };

  const handleNicknameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setNickname(e.target.value);
    setError(undefined);
  };

  return (
    <div className={cx("portal", { visible: isOpen })}>
      <div className={styles.background} />
      <form
        className={styles.nicknameModal}
        onSubmit={(e) => {
          e.preventDefault();
          handleContinue();
        }}
      >
        <Header style={{ color: nicknameQuestionColor }} className={styles.header} variant="h6">
          {nicknameQuestion}
        </Header>
        <Paragraph color="secondary" size="small" className={styles.info}>
          {projectId ? t("nicknameModal.projectLeaderboardInfo") : t("nicknameModal.info")}
        </Paragraph>
        <Header className={styles.label} variant="h6" color="neutrals-600">
          {nicknameLabel}
        </Header>
        <TextInput
          className={styles.input}
          value={nickname}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={true}
          onChange={handleNicknameChange}
          size={isDesktop ? "medium" : "small"}
          icon={<NicknameIcon />}
          error={error}
        />
        {error && (
          <Paragraph size="xs" className={styles.error}>
            {error}
          </Paragraph>
        )}
        <Button type="submit" className={styles.submit} icon={<ArrowRightIcon />} iconSide="left">
          {t("common:continue")}
        </Button>
      </form>
    </div>
  );
};
